"use client";
import type { OnNodesChange } from "@xyflow/react";
import { applyNodeChanges } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RunProgress } from "@/types/aicheck";
import { buildGraphFromServerSkeleton } from "../graph-builder";
import { computeLayout } from "../layout";
import { mapProgressToNodeData } from "../state-mapper";
import type { PipelineEdge, PipelineNode, PipelineNodeData } from "../types";

/** `next` が持つキーがすべてノードの現在値と同じか。 */
function nodeDataMatches(
  current: Record<string, unknown>,
  next: Record<string, unknown>,
): boolean {
  for (const key of Object.keys(next)) {
    if (current[key] !== next[key]) return false;
  }
  return true;
}

/**
 * ノードへ最新の状態を載せる。**純粋関数であること自体が要件。**
 *
 * React は setState に渡した更新関数を**同じ入力で複数回呼ぶことがある**。
 * 以前はここで「前回配った値」を覚えたモジュール外の ref と比べていたため、
 * 一度目の実行後にその ref が書き換わり、React が同じ更新関数をもう一度
 * 実行したときには全ノードが「前回と同じ」と判定され、`prev`（＝スケルトンの
 * planned＝灰色）がそのまま返っていた。中断後にグラフを開き直すと全部灰色、
 * という形で表に出た。
 *
 * 比べる相手は**ノード自身の今の値**にする。そうすれば何度実行しても
 * 結果は同じで、判定が表示とずれることが原理的に起きない。
 */
export function applyNodeData(
  nodes: PipelineNode[],
  nodeDataMap: Map<string, Partial<PipelineNodeData>>,
): PipelineNode[] {
  let changed = false;
  const updated = nodes.map((node) => {
    const newData = nodeDataMap.get(node.id);
    if (!newData) return node;
    if (
      nodeDataMatches(
        node.data as Record<string, unknown>,
        newData as Record<string, unknown>,
      )
    ) {
      return node;
    }
    changed = true;
    return {
      ...node,
      data: { ...node.data, ...newData } as PipelineNode["data"],
    };
  });
  return changed ? updated : nodes;
}

export function usePipelineGraph(
  progress: RunProgress,
  direction: "TB" | "LR" = "TB",
) {
  // === Layer 1: Skeleton rebuild (low frequency) ===
  const {
    initialNodes,
    initialEdges,
    graphHeight,
    graphWidth,
    existingNodeIds,
  } = useMemo(() => {
    if (!progress.graphSkeleton) {
      throw new Error(
        "Missing server graph-skeleton. Client-side graph generation is disabled.",
      );
    }
    const skeleton = buildGraphFromServerSkeleton(progress.graphSkeleton);
    const laid = computeLayout(skeleton.nodes, skeleton.edges, direction);
    const nodeIds = new Set(laid.nodes.map((n) => n.id));
    return {
      initialNodes: laid.nodes,
      initialEdges: laid.edges,
      graphHeight: laid.graphHeight,
      graphWidth: laid.graphWidth,
      existingNodeIds: nodeIds,
    };
  }, [progress.graphSkeleton, direction]);

  const [nodes, setNodes] = useState<PipelineNode[]>(initialNodes);
  const [edges, setEdges] = useState<PipelineEdge[]>(initialEdges);
  const [skeletonVersion, setSkeletonVersion] = useState(0);
  const prevSkeletonRef = useRef(progress.graphSkeleton);

  // Reset when skeleton changes.
  // Keep node status/progress only when this is a pure direction toggle
  // (same server skeleton object). For a newly received skeleton, start from
  // server-defined baseline (planned) to avoid stale "all green" flashes.
  useEffect(() => {
    const isDirectionOnlyUpdate =
      prevSkeletonRef.current === progress.graphSkeleton;
    prevSkeletonRef.current = progress.graphSkeleton;
    setNodes((prev) => {
      // server baseline（planned）へ戻す。ここで捨てた表示が次の塗り直しで
      // 戻ってくることは applyNodeData が保証する（ノード自身と比べるため、
      // 「捨てたこと」を別に覚えておく必要がない）。
      if (!isDirectionOnlyUpdate) return initialNodes;
      const prevDataMap = new Map(prev.map((n) => [n.id, n.data]));
      return initialNodes.map((node) => {
        const prevData = prevDataMap.get(node.id);
        return prevData
          ? { ...node, data: prevData as PipelineNode["data"] }
          : node;
      });
    });
    setEdges(initialEdges);
    setSkeletonVersion((v) => v + 1);
  }, [initialNodes, initialEdges, progress.graphSkeleton]);

  // === Layer 2: Node data updates (1fps throttle) ===
  const rafRef = useRef<number>(0);
  const lastUpdateRef = useRef<number>(0);

  useEffect(() => {
    const now = performance.now();
    const elapsed = now - lastUpdateRef.current;
    const delay = Math.max(0, 1000 - elapsed); // 1fps = 1000ms

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const doUpdate = () => {
      lastUpdateRef.current = performance.now();
      const nodeDataMap = mapProgressToNodeData(progress, existingNodeIds);

      setNodes((prev) => applyNodeData(prev, nodeDataMap));

      // Update edge statuses based on source node status
      setEdges((prev) => {
        let changed = false;
        const updated = prev.map((edge) => {
          const sourceData = nodeDataMap.get(edge.source);
          const newStatus = sourceData?.status ?? "planned";
          const currentStatus =
            (edge.data as { status: string } | undefined)?.status ?? "planned";
          if (newStatus === currentStatus) return edge;
          changed = true;
          return { ...edge, data: { status: newStatus } };
        });
        return changed ? updated : prev;
      });
    };

    if (delay === 0) {
      rafRef.current = requestAnimationFrame(doUpdate);
    } else {
      const timer = setTimeout(() => {
        rafRef.current = requestAnimationFrame(doUpdate);
      }, delay);
      return () => clearTimeout(timer);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [progress, existingNodeIds]);

  const onNodesChange: OnNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds) as PipelineNode[]);
  }, []);

  return {
    nodes,
    edges,
    onNodesChange,
    graphHeight,
    graphWidth,
    skeletonVersion,
  };
}
